/**
 * DEV-ONLY Spatial Operations fixture view-models.
 * Production SpatialOperationsPage must never import this module.
 */
import { buildVisualLifecycleModel } from '../../domain/continuousSafety/visualLifecycleModel';
import type { ConditionLifecycleSummary } from '../../domain/continuousSafety/siteConditionState';
import {
  presentSpatialOperationsScan,
  presentSpatialOperationsStages,
  type SpatialOperationsCameraChoice,
  type SpatialOperationsConditionChoice,
} from '../../domain/spatialOperations/spatialOperationsPresentation';
import type { SpatialOperationsPolicyView } from '../../domain/spatialOperations/spatialOperationsPolicy';
import type { SpatialOperationsShellProps } from '../../components/spatialOperations/SpatialOperationsShell';
import type { CanonicalReportLifecycle } from '../../domain/safetyScanReport/canonicalSafetyScanReportPresenter';
import type { OverviewAreaCameraPanelModel } from '../OverviewAreaCameraPanel';

export const FULL_LIFECYCLE_SCAN_WINDOW = {
  start: '2026-09-08T13:00:00.000Z',
  end: '2026-09-08T20:00:00.000Z',
} as const;

/** Stored Scan facts for the after-Control window. Rate is stored, not presenter-computed. */
export const FULL_LIFECYCLE_SCAN_MEASUREMENT = {
  confirmed: 4,
  reviewed: 4,
  usableHours: 7,
  /** 4 confirmed / 7 usable hours, rounded to 3 decimals to match stored-rate authority. */
  rate: 0.571,
} as const;

export function scheduledScanWindowHours(start: string, end: string): number {
  const startMs = Date.parse(start);
  const endMs = Date.parse(end);
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) return 0;
  return (endMs - startMs) / 3_600_000;
}

export function fullLifecycleScanFixtureIssues(): string[] {
  const issues: string[] = [];
  const windowHours = scheduledScanWindowHours(
    FULL_LIFECYCLE_SCAN_WINDOW.start,
    FULL_LIFECYCLE_SCAN_WINDOW.end,
  );
  const { confirmed, reviewed, usableHours, rate } = FULL_LIFECYCLE_SCAN_MEASUREMENT;
  if (usableHours > windowHours + 1e-6) {
    issues.push(`usable_hours ${usableHours} exceeds scheduled window ${windowHours}`);
  }
  if (confirmed > reviewed) {
    issues.push(`confirmed ${confirmed} exceeds reviewed ${reviewed}`);
  }
  if (usableHours > 0) {
    const implied = confirmed / usableHours;
    const tolerance = Math.max(0.05, Math.abs(implied) * 0.05);
    if (Math.abs(rate - implied) > tolerance) {
      issues.push(`stored rate ${rate} does not match ${confirmed}/${usableHours}`);
    }
  }
  const controlAt = Date.parse('2026-09-08T13:00:00Z');
  const patternAt = Date.parse('2026-09-08T09:05:00Z');
  const scanEnd = Date.parse(FULL_LIFECYCLE_SCAN_WINDOW.end);
  if (!(patternAt < controlAt && controlAt <= scanEnd)) {
    issues.push('pattern / Control / Scan timestamps are not mutually ordered');
  }
  return issues;
}

export type SpatialOperationsFixtureScenario =
  | 'find_only'
  | 'alerted'
  | 'control_active'
  | 'full_lifecycle'
  | 'no_geometry'
  | 'camera_offline'
  | 'live_fallback'
  | 'scan_draft'
  | 'scan_verified'
  | 'scan_signed'
  | 'core_conditions';

const SNAPSHOT = `data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 360"><rect width="640" height="360" fill="#d8dee8"/><text x="24" y="40" font-size="22" font-family="Arial" fill="#0f172a">Fixture current camera view</text></svg>')}`;
const LIVE_FIXTURE = `data:image/svg+xml;charset=utf-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 854 480"><rect width="854" height="480" fill="#1b2433"/><text x="28" y="48" font-size="26" font-family="Arial" fill="#e8eef7">Fixture live camera</text><text x="28" y="84" font-size="16" font-family="Arial" fill="#9fb0c8">Not a production stream</text></svg>')}`;

const cameras: SpatialOperationsCameraChoice[] = [
  {
    cameraId: 'cam_walkway',
    deviceId: 'edge-1',
    areaId: 'walkway',
    areaLabel: 'Warehouse Room B',
    cameraLabel: 'Walkway camera',
    mountLabel: 'Walkway',
  },
];

const conditions: SpatialOperationsConditionChoice[] = [
  {
    conditionId: 'cond-forklift',
    family: 'PERSON_FORKLIFT_PROXIMITY',
    label: 'Forklift-pedestrian proximity',
    cameraId: 'cam_walkway',
    zoneId: 'zone-walkway',
    incidentType: 'near_miss',
  },
  {
    conditionId: 'cond-rae',
    family: 'RESTRICTED_AREA_ENTRY',
    label: 'Restricted-area entry',
    cameraId: 'cam_walkway',
    zoneId: 'zone-walkway',
    incidentType: 'zone_violation',
  },
  {
    conditionId: 'cond-ppe',
    family: 'PPE_NON_COMPLIANCE',
    label: 'PPE presence',
    cameraId: 'cam_walkway',
    zoneId: 'zone-walkway',
    incidentType: 'ppe_violation',
  },
];

function lifecycle(kind: SpatialOperationsFixtureScenario): ConditionLifecycleSummary {
  const none: ConditionLifecycleSummary = {
    latestImmediateIncident: null,
    latestTemporalPatternIncident: null,
    motivatingIncidentIds: [],
    controlRevisionId: null,
    sinceStartedAt: null,
    latestScan: null,
    lifecycleHref: null,
    lifecycleUiPath: '/acceptance/lifecycle?site_id=yyz&condition_id=cond-forklift',
    populationExplanation: null,
    postControlPattern: null,
  };
  const alerts: ConditionLifecycleSummary = {
    ...none,
    latestImmediateIncident: {
      incidentId: 'imm-1',
      triggerKind: 'IMMEDIATE',
      severity: 'high',
      state: 'OPEN',
      occurredAt: '2026-09-08T09:00:00Z',
      observedCount: null,
      patternRule: null,
      patternMinEvents: null,
      patternWindowSeconds: null,
    },
    latestTemporalPatternIncident: {
      incidentId: 'pat-1',
      triggerKind: 'TEMPORAL_PATTERN',
      severity: 'high',
      state: 'OPEN',
      occurredAt: '2026-09-08T09:05:00Z',
      observedCount: 5,
      patternRule: '5 in 3600s',
      patternMinEvents: 5,
      patternWindowSeconds: 3600,
    },
    motivatingIncidentIds: ['imm-1', 'pat-1'],
  };
  const control: ConditionLifecycleSummary = {
    ...alerts,
    controlRevisionId: 'control-1',
    sinceStartedAt: '2026-09-08T13:00:00Z',
    postControlPattern: {
      status: 'none_in_measured_period',
      count: 0,
      latestIncidentId: null,
      latestOccurredAt: null,
      coverageState: null,
      finality: null,
      summary: 'No Pattern Alerts after Control in the measured period.',
    },
  };
  const full: ConditionLifecycleSummary = {
    ...control,
    latestScan: {
      scanDefinitionId: 'scan-1',
      controlRevisionId: 'control-1',
      scheduledRunId: 'run-1',
      beforeReportId: 'before-1',
      afterReportId: 'after-1',
      runStatus: 'COMPLETED',
      resultDirection: 'decreased',
      coverageState: kind === 'control_active' ? 'insufficient' : 'sufficient',
      finality: 'finalized',
      evaluatedAt: '2026-09-08T20:00:00Z',
      motivatingIncidents: [{ incidentId: 'pat-1', triggerKind: 'TEMPORAL_PATTERN' }],
    },
  };
  if (kind === 'find_only' || kind === 'no_geometry' || kind === 'camera_offline' || kind === 'core_conditions') return none;
  if (kind === 'alerted') return alerts;
  if (kind === 'control_active') return { ...control, latestScan: null };
  return full;
}

function scanLifecycleFor(kind: SpatialOperationsFixtureScenario): CanonicalReportLifecycle | null {
  if (kind === 'scan_draft') return 'Draft';
  if (kind === 'scan_verified' || kind === 'full_lifecycle' || kind === 'live_fallback') return 'Verified';
  if (kind === 'scan_signed') return 'Signed';
  if (kind === 'control_active') return null;
  return null;
}

function cameraPanel(offline: boolean): OverviewAreaCameraPanelModel {
  return {
    areaTitle: 'Warehouse Room B · Walkway',
    cameraLabel: 'Walkway camera',
    cameraId: 'cam_walkway',
    deviceId: 'edge-1',
    zoneLabel: 'Room B walkway zone',
    zoneId: 'zone-walkway',
    snapshotUrl: offline ? null : SNAPSHOT,
    geometry: {
      zoneId: 'zone-walkway',
      label: 'Room B walkway zone',
      polygon: [[0.12, 0.18], [0.88, 0.18], [0.88, 0.78], [0.12, 0.78]],
      deployed: true,
      conditionId: 'cond-forklift',
      measurementScopeKind: 'ZONE_POLYGON',
    },
    overlays: [{
      zoneId: 'zone-walkway',
      label: 'Room B walkway zone',
      polygon: [[0.12, 0.18], [0.88, 0.18], [0.88, 0.78], [0.12, 0.78]],
      deployed: true,
      conditionId: 'cond-forklift',
      measurementScopeKind: 'ZONE_POLYGON',
    }],
    cameraPath: '/cameras/cam_walkway?site=yyz',
    livePath: '/live-view?site=yyz&camera_id=cam_walkway&device_id=edge-1',
    zonePath: '/zones?site_id=yyz&zone_id=zone-walkway&camera_id=cam_walkway',
    areaName: 'Warehouse Room B',
    mountLabel: 'Walkway',
  };
}

const policy: SpatialOperationsPolicyView = {
  conditionLabel: 'Forklift-pedestrian proximity',
  scopeLabel: '1 zone · camera scoped',
  severity: 'high',
  immediateEnabled: true,
  patternEnabled: true,
  patternRule: '5 qualifying occurrences within 60 minutes',
  scheduleLabel: 'Always active',
};

export function buildSpatialOperationsFixtureProps(
  scenario: SpatialOperationsFixtureScenario,
  overrides: Partial<SpatialOperationsShellProps> = {},
): SpatialOperationsShellProps {
  const life = lifecycle(scenario);
  const scanState = scanLifecycleFor(scenario);
  const hasGeometry = scenario !== 'no_geometry';
  const visualModel = buildVisualLifecycleModel({
    lifecycle: life,
    condition: {
      conditionId: 'cond-forklift',
      conditionFamily: 'PERSON_FORKLIFT_PROXIMITY',
      canonicalScope: { areaId: 'walkway', zoneIds: hasGeometry ? ['zone-walkway'] : [], cameraIds: ['cam_walkway'] },
    } as any,
    detail: {
      condition_id: 'cond-forklift',
      condition_family: 'PERSON_FORKLIFT_PROXIMITY',
      occurrences: scenario === 'find_only' || scenario === 'no_geometry' || scenario === 'camera_offline' || scenario === 'core_conditions'
        ? []
        : [{ occurrence_id: 'occ-1', occurred_at: '2026-09-08T08:50:00Z', zone_id: 'zone-walkway' }],
      incidents: life.latestImmediateIncident ? [
        { incident_id: 'imm-1', trigger_kind: 'IMMEDIATE', ts_start: '2026-09-08T09:00:00Z', severity: 'high' },
        {
          incident_id: 'pat-1',
          trigger_kind: 'TEMPORAL_PATTERN',
          ts_start: '2026-09-08T09:05:00Z',
          observed_count: 5,
          envelope_metadata: { min_events: 5, window_seconds: 3600 },
          source_occurrence_ids: ['occ-1', 'occ-2'],
          threshold_occurrence_id: 'occ-2',
        },
      ] : [],
      controls: life.controlRevisionId ? [{
        control_revision_id: 'control-1',
        implemented_at: '2026-09-08T13:00:00Z',
        implementation_state: 'implemented',
        control_objective: 'Separate pedestrian walkway from forklift travel.',
      }] : [],
      scans: life.latestScan ? [{
        after_report_id: 'after-1',
        before_report_id: 'before-1',
        evaluated_at: '2026-09-08T20:00:00Z',
        result_direction: 'decreased',
        coverage_state: life.latestScan.coverageState,
        finality: 'finalized',
      }] : [],
    },
    route: {
      siteId: 'yyz',
      conditionId: 'cond-forklift',
      areaId: 'walkway',
      zoneId: hasGeometry ? 'zone-walkway' : null,
      cameraId: 'cam_walkway',
      conditionFamily: 'PERSON_FORKLIFT_PROXIMITY',
    },
    zones: hasGeometry ? [{
      zone_id: 'zone-walkway',
      name: 'Room B walkway zone',
      polygon_norm: [[0.12, 0.18], [0.88, 0.18], [0.88, 0.78], [0.12, 0.78]],
    } as any] : [],
    evidence: { status: 'empty', imageUrl: null, incidentId: null },
  });
  const stages = presentSpatialOperationsStages({
    conditionLabel: 'Forklift-pedestrian proximity',
    areaLabel: 'Warehouse Room B',
    zoneLabel: 'Room B walkway zone',
    current: scenario === 'find_only' || scenario === 'no_geometry' || scenario === 'camera_offline' || scenario === 'core_conditions'
      ? null
      : ({
        conditionId: 'cond-forklift',
        measurementValue: 2,
        bodyLine: 'Observed in the current window.',
        supportingActivityLabel: 'Current activity recorded',
      } as any),
    recurring: scenario === 'find_only' || scenario === 'no_geometry' || scenario === 'camera_offline' || scenario === 'core_conditions'
      ? null
      : ({
        conditionId: 'cond-forklift',
        recordedOccurrenceTotal: 11,
        recordedOccurrenceLabel: '11 recorded occurrences',
      } as any),
    lifecycle: life,
    scanLifecycle: scanState,
    siteId: 'yyz',
    conditionId: 'cond-forklift',
    areaId: 'walkway',
    zoneId: hasGeometry ? 'zone-walkway' : null,
    cameraId: 'cam_walkway',
    canOpenScan: Boolean(life.latestScan?.afterReportId),
    timeLabel: (value) => {
      if (!value) return null;
      const ms = Date.parse(value);
      if (!Number.isFinite(ms)) return value;
      return new Date(ms).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        timeZone: 'America/Toronto',
      });
    },
  });
  const scan = presentSpatialOperationsScan({
    lifecycle: life,
    scanLifecycle: scanState,
    confirmedLabel: scanState ? `${FULL_LIFECYCLE_SCAN_MEASUREMENT.confirmed} confirmed` : null,
    usableHoursLabel: scanState ? `${FULL_LIFECYCLE_SCAN_MEASUREMENT.usableHours} h usable` : null,
    siteId: 'yyz',
    conditionId: 'cond-forklift',
    areaId: 'walkway',
    zoneId: hasGeometry ? 'zone-walkway' : null,
    cameraId: 'cam_walkway',
    canOpenScan: Boolean(life.latestScan?.afterReportId),
  });
  const noop = () => undefined;
  return {
    binding: 'fixture',
    siteId: 'yyz',
    siteLabel: 'Toronto warehouse',
    cameras,
    conditions,
    selectedCamera: cameras[0],
    selectedCondition: conditions[0],
    mode: 'camera',
    cameraPanel: cameraPanel(scenario === 'camera_offline'),
    liveCamera: scenario === 'camera_offline'
      ? { status: 'offline' as const, mjpegUrl: null }
      : scenario === 'live_fallback'
        ? { status: 'fallback_snapshot' as const, mjpegUrl: null }
        : { status: 'live' as const, mjpegUrl: LIVE_FIXTURE },
    cameraOnline: scenario === 'camera_offline' ? false : true,
    cameraFreshness: scenario === 'camera_offline' ? 'Camera offline' : 'Fresh',
    visualModel,
    stages,
    scan,
    policy,
    recordedEvidenceUrl: null,
    recordedEvidenceHref: null,
    timeZone: 'America/Toronto',
    onNavigate: noop,
    onSelectCamera: noop,
    onSelectCondition: noop,
    onSelectMode: noop,
    onSelectStage: noop,
    ...overrides,
  };
}

export function parseFixtureScenario(search: string): SpatialOperationsFixtureScenario {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const value = String(params.get('scenario') || 'full_lifecycle').trim();
  const allowed: SpatialOperationsFixtureScenario[] = [
    'find_only', 'alerted', 'control_active', 'full_lifecycle', 'no_geometry',
    'camera_offline', 'live_fallback', 'scan_draft', 'scan_verified', 'scan_signed', 'core_conditions',
  ];
  return (allowed as string[]).includes(value) ? value as SpatialOperationsFixtureScenario : 'full_lifecycle';
}
